import type { AnchorRef, CanvasElement, ConnectorElement } from '@archer/shared';

interface Pt {
  x: number;
  y: number;
}

export const getShapeAnchor = (
  el: CanvasElement,
  anchor: AnchorRef['anchor'],
  fallback: Pt = { x: el.x + el.width / 2, y: el.y + el.height / 2 },
): Pt => {
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  let a = anchor;
  if (a === 'auto') {
    if (el.width > 0 && el.height > 0) {
      const dx = fallback.x - cx;
      const dy = fallback.y - cy;
      if (Math.abs(dx) / el.width > Math.abs(dy) / el.height) {
        a = dx > 0 ? 'right' : 'left';
      } else {
        a = dy > 0 ? 'bottom' : 'top';
      }
    } else {
      a = 'right';
    }
  }
  switch (a) {
    case 'top':
      return { x: cx, y: el.y };
    case 'right':
      return { x: el.x + el.width, y: cy };
    case 'bottom':
      return { x: cx, y: el.y + el.height };
    case 'left':
      return { x: el.x, y: cy };
    default:
      return { x: cx, y: cy };
  }
};

export const getConnectorPoints = (el: ConnectorElement, elements: CanvasElement[]): number[] => {
  const pts = [...el.points];
  const sourceEl = el.source?.elementId ? elements.find((e) => e.id === el.source!.elementId) : undefined;
  const targetEl = el.target?.elementId ? elements.find((e) => e.id === el.target!.elementId) : undefined;
  if (sourceEl && pts.length >= 2) {
    const fallback = { x: pts[pts.length - 2] ?? 0, y: pts[pts.length - 1] ?? 0 };
    const p = getShapeAnchor(sourceEl, el.source!.anchor, fallback);
    pts[0] = p.x;
    pts[1] = p.y;
  }
  if (targetEl && pts.length >= 2) {
    const fallback = { x: pts[0] ?? 0, y: pts[1] ?? 0 };
    const p = getShapeAnchor(targetEl, el.target!.anchor, fallback);
    pts[pts.length - 2] = p.x;
    pts[pts.length - 1] = p.y;
  }
  return pts;
};

export const distToSegment = (p: Pt, a: Pt, b: Pt): number => {
  const v = { x: b.x - a.x, y: b.y - a.y };
  const w = { x: p.x - a.x, y: p.y - a.y };
  const c1 = v.x * w.x + v.y * w.y;
  const c2 = v.x * v.x + v.y * v.y;
  if (c2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const t = Math.max(0, Math.min(1, c1 / c2));
  return Math.hypot(p.x - (a.x + t * v.x), p.y - (a.y + t * v.y));
};

/** Hit-test a point against a shape element's geometry. */
export const hitTest = (
  el: CanvasElement,
  pt: Pt,
): boolean => {
  const { x, y, width: w, height: h } = el;
  if (el.type === 'ellipse') {
    const cx = x + w / 2;
    const cy = y + h / 2;
    const dx = (pt.x - cx) / (w / 2);
    const dy = (pt.y - cy) / (h / 2);
    return dx * dx + dy * dy <= 1;
  }
  if (el.type === 'arrow' || el.type === 'line') {
    const c = el as ConnectorElement;
    const pts = c.points ?? [];
    for (let i = 0; i < pts.length - 2; i += 2) {
      const a = { x: pts[i]!, y: pts[i + 1]! };
      const b = { x: pts[i + 2]!, y: pts[i + 3]! };
      if (distToSegment(pt, a, b) < 6) return true;
    }
    return false;
  }
  return pt.x >= x && pt.x <= x + w && pt.y >= y && pt.y <= y + h;
};

export const snap = (value: number, grid: number): number => Math.round(value / grid) * grid;
